import React, { FunctionComponent } from 'react';
import { Input, Button, Grid } from '@inplayer-org/inplayer-ui';

// hooks
import { useInput } from 'hooks/useInput';

// components
import StyledContainer from 'components/StyledContainer';
import ErrorMessage from 'components/ErrorMessage';
import { BlueTypography } from 'components/SharedStyledElements';
import AssetTypeCard from '../components/AssetTypeCard';

// types
import { AssetType } from '../assetTypes';

const { Container, Cell } = Grid;

interface AssetTypeOption {
  type: AssetType;
  icon: string;
  title: string;
  description: string;
}

const assetTypeOptions: Array<AssetTypeOption> = [
  {
    type: AssetType.Video,
    icon: 'video',
    title: 'Video',
    description: 'Add content from your Online Video Platform account.',
  },
  {
    type: AssetType.Html,
    icon: 'scriptTags',
    title: 'HTML',
    description: 'Add content by just using HTML or an embed code.',
  },
  {
    type: AssetType.JWOTT,
    icon: 'file',
    title: 'JW OTT',
    description: 'The JW OTT Webapp is an open-source, dynamically generated video website.',
  },
];

interface Props {
  assetTitle: string;
  assetType: string;
  displayErrorMessage?: boolean;
  onAssetTypeSelect: (assetType: string) => void;
  onSaveBasicAssetInfo: (assetName: string) => void;
  presetAssetTitle?: string;
  isTemplate?: boolean;
  stepTitle: string;
}

const InitialAssetCreate: FunctionComponent<Props> = ({
  onSaveBasicAssetInfo,
  assetTitle,
  assetType,
  onAssetTypeSelect,
  displayErrorMessage,
  presetAssetTitle,
  isTemplate = false,
}) => {
  const assetTitleInput = useInput(presetAssetTitle || assetTitle);
  const displayAssetErrorMessage = displayErrorMessage && assetTitleInput.value.length < 3;
  const displayAssetSecondErrorMessage = displayErrorMessage && assetTitleInput.value.length > 191;
  const namePlaceholder = isTemplate ? 'Template' : 'Asset';

  return (
    <>
      {!presetAssetTitle && (
        <>
          <Input
            tag={`text_input_${namePlaceholder.toLocaleLowerCase()}_name`}
            type="text"
            placeholder={`${namePlaceholder} name`}
            {...assetTitleInput.eventBind}
          />
          <br />
          {displayAssetErrorMessage && (
            <ErrorMessage>{namePlaceholder} title should be longer than 2 characters.</ErrorMessage>
          )}
          {displayAssetSecondErrorMessage && (
            <ErrorMessage>{namePlaceholder} title should not be longer than 191 characters.</ErrorMessage>
          )}
        </>
      )}
      <BlueTypography variant="h5">Select {namePlaceholder.toLowerCase()} type</BlueTypography>
      <br />
      <Container gap="2rem 1rem">
        {assetTypeOptions.map((asset) => (
          <Cell key={asset.type} width={6}>
            <AssetTypeCard
              {...asset}
              tag={`box_${asset.type}`}
              isSelected={assetType === asset.type}
              onAssetTypeSelect={onAssetTypeSelect}
              height="6.5rem"
            />
          </Cell>
        ))}
      </Container>
      <StyledContainer columns="1fr 1f" margin="2rem 0 0 0">
        <Cell left="3">
          <Button
            tag="button_next"
            fullWidth
            type="submit"
            disabled={!(assetTitleInput.hasValue && assetType)}
            onClick={() => onSaveBasicAssetInfo(assetTitleInput.value)}
            buttonModifiers={['buttonPrimary']}
          >
            Next
          </Button>
        </Cell>
      </StyledContainer>
    </>
  );
};

export default InitialAssetCreate;
